import { cn } from '@altify/components';
import clsx from 'clsx';
import React from 'react';
import { useExpanded, useTable } from 'react-table';

export type AdvancedTableCell<T> = {
  value: any;
  row: {
    original: T;
  };
};

export const AdvancedTable = ({
  data,
  columns,
  padded,
  onClickRow,
  renderRowSubComponent,
  noDataMessage,
  serverErrorMessage,
  headerColor,
  isScrollable = false,
  padContent = true
}: {
  data: any[];
  columns: any[];
  padded?: boolean;
  padContent?: boolean;
  onClickRow?: Function | null;
  canExpand?: boolean;
  renderRowSubComponent?: Function | null;
  noDataMessage?: string;
  serverErrorMessage?: any;
  /** this has to be a color that works with tailwind (e.g. `bg-red-500` ) */
  headerColor?: string;
  isScrollable?: boolean;
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: {
      // @ts-ignore
      expanded,
    },
  } = useTable(
    {
      columns,
      data,
      // @ts-ignore
      initialState: { pageIndex: 1 },
    },
    useExpanded,
  );

  return (
    <div className="w-full relative">
      <div>
        <table
          {...getTableProps()}
          className={clsx(
            `w-full table-fixed p-2 border-separate ${
              headerColor || ''
            } rounded-tl-2xl rounded-tr-2xl`,
          )}
          style={{
            borderSpacing: '0px 4px',
          }}>
          <thead className="rounded-lg">
            {headerGroups.map((headerGroup, hIndex: number) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={hIndex}>
                {headerGroup.headers.map((column, i) => (
                  <th
                    {...column.getHeaderProps()}
                    className={clsx(
                      'text-body-text text-sm text-left px-3 py-2 font-normal',
                      {
                        'rounded-l-lg': i === 0,
                        'rounded-r-lg': i === columns.length - 1,
                      },
                    )}>
                    <div
                      className={cn('flex flex-row items-center', {
                        'rounded-l-lg': i === 0,
                        'rounded-r-lg': i === columns.length - 1,
                        'text-right justify-end':
                          columns[i].alignHeader === 'right',
                        'text-center justify-center':
                          columns[i].alignHeader === 'center',
                      })}>
                      {column.render('Header')}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
        </table>
      </div>

      <div className={`h-full ${isScrollable ? 'overflow-auto' : ''}`}>
        <table
          className={clsx('w-full border-separate table-fixed pt-0', {
            'pb-20': padded,
            'p-2': padContent,
          })}
          style={{
            borderSpacing: '0px 4px',
          }}>
          <tbody {...getTableBodyProps()}>
            {rows.length <= 0 && (
              <tr className="mt-4 w-full text-center">
                <td
                  style={{
                    verticalAlign: 'middle',
                    padding: '1.25rem',
                  }}
                  className={clsx(
                    'bg-background mt-4 text-body-text text-center whitespace-whitespace-pre-wrap rounded-l-lg rounded-r-lg',
                  )}>
                  {serverErrorMessage || noDataMessage}
                </td>
              </tr>
            )}

            {rows.length > 0 &&
              rows.map((row, i) => {
                prepareRow(row);
                const rowProps = row.getRowProps();
                return (
                  <React.Fragment key={i}>
                    <tr
                      {...rowProps}
                      onClick={() => onClickRow?.(row.original)}
                      className={clsx(
                        'mt-4',
                        onClickRow !== undefined && 'cursor-pointer',
                      )}
                      // style={{
                      //     borderBottom: '1px solid #FFF',
                      // }}
                    >
                      {row.cells.map((cell, c) => {
                        const lastIndex = row.cells.length - 1;

                        return (
                          <td
                            {...cell.getCellProps()}
                            style={{
                              verticalAlign: 'middle',
                              padding: '1.25rem 0.75rem',
                              borderBottom: '1px solid #2F3A5C',
                            }}
                            className={clsx(
                              'bg-background mx-4 text-header-text w-full whitespace-whitespace-pre-wrap',
                              {
                                'rounded-l-none': c === 0,
                                'rounded-r-none': c === lastIndex,
                              },
                            )}>
                            {cell.render('Cell')}
                          </td>
                        );
                      })}
                    </tr>
                    {/* @ts-ignore */}
                    {(row.isExpanded || expanded[i]) && (
                      <tr>
                        <td colSpan={columns.length}>
                          {renderRowSubComponent?.({ row, rowProps })}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
